import React from 'react';
import { Link } from 'react-router-dom';

export default function KloakspulingContentSection() {
  const warningSigns = [
    {
      title: 'Vandet løber langsommere ud end normalt',
      desc: 'Et tidligt tegn på at fedt, sæbe eller slam gradvist forsnævrer rørets indvendige diameter.',
      icon: 'fa-duotone fa-faucet-drip',
      badge: 'Gennemløb'
    },
    {
      title: 'Flere afløb giver gurglende lyde',
      desc: 'Når vandet skal kæmpe for at passere, dannes der undertryk og luftbobler i rørsystemet.',
      icon: 'fa-duotone fa-waveform-lines',
      badge: 'Rørsystem & Luft'
    },
    {
      title: 'Der kommer ubehagelig lugt fra kloakken',
      desc: 'Lugtgener opstår, når stillestående organisk materiale rådner og presses tilbage mod boligen.',
      icon: 'fa-duotone fa-wind',
      badge: 'Lugtgener'
    },
    {
      title: 'Vandet står stille, og der er tegn på en stoppet kloak',
      desc: 'En reel blokering har lukket passagen til – her kræves hurtig indsats for at undgå oversvømmelse.',
      icon: 'fa-duotone fa-water-lower',
      badge: 'Stoppet kloak'
    }
  ];

  const processSteps = [
    {
      step: '01',
      phase: 'Fase 1',
      title: 'Først vurderer vi problemet og adgangen',
      text: 'Vi begynder med at høre, hvad du har oplevet, og hvor problemet viser sig. Derefter vurderer vi adgangsforholdene og den mest hensigtsmæssige løsning. Søger du på spuling kloak, er det netop denne faglige vurdering, der gør forskellen mellem en kortvarig åbning og en grundig rens.',
      icon: 'fa-duotone fa-magnifying-glass-location'
    },
    {
      step: '02',
      phase: 'Fase 2',
      title: 'Derefter renser vi ledningen systematisk',
      text: 'Når adgangen er fundet, spuler vi ledningen grundigt og holder øje med, om gennemløbet bliver stabilt. Ved behov kan slamsugning indgå, så løst materiale bliver fjernet fra brønde eller tanke. Du får en løsning, der passer til den konkrete kloak frem for en fast standardbehandling.',
      icon: 'fa-duotone fa-spray-can-sparkles'
    },
    {
      step: '03',
      phase: 'Fase 3',
      title: 'Til sidst ser vi efter tegn på en større skade',
      text: 'Hvis problemet vender tilbage, kan der være behov for TV inspektion. Den kan vise brud, forskudte samlinger eller andre forhold, som almindelig spuling ikke kan løse. Hvis der er brug for en reparation, kan vi som autoriseret kloakmester rådgive om det videre kloakarbejde.',
      icon: 'fa-duotone fa-camera-cctv'
    }
  ];

  const preventionTips = [
    {
      tip: 'Fjern jævnligt hår og snavs fra riste og vandlåse',
      detail: 'Mekanisk rensning af dine overfladeafløb forhindrer snavset i at nå dybere ned i rørene.',
      icon: 'fa-solid fa-broom'
    },
    {
      tip: 'Undgå at hælde fedt og madrester i køkkenvasken',
      detail: 'Varmt fedt størkner lynhurtigt i de kolde jordrør og danner hårde, uigennemtrængelige propper.',
      icon: 'fa-solid fa-ban'
    },
    {
      tip: 'Hold øje med brønde og afløb efter kraftig regn',
      detail: 'Tjek om vandet forsvinder som det skal, eller om overfladevand stuver op i dine sandfangsbrønde.',
      icon: 'fa-solid fa-cloud-showers-heavy'
    },
    {
      tip: 'Reager på lugt, langsomt afløb og gentagne blokeringer',
      detail: 'Vent ikke til kloakken er helt lukket – rettidig spuling forhindrer vandskader og oversvømmede gulve.',
      icon: 'fa-solid fa-triangle-exclamation'
    }
  ];

  const priceFactors = [
    {
      title: 'Ledningens længde & dimension',
      desc: 'Hvor mange meter rør der skal spules igennem, samt rørets tykkelse og materiale.',
      icon: 'fa-solid fa-ruler-horizontal'
    },
    {
      title: 'Adgangsforhold til rør & brønde',
      desc: 'Placeringen af rensebrønde, dæksler og adgangsveje for slanger og spuleudstyr.',
      icon: 'fa-solid fa-truck-ramp-couch'
    },
    {
      title: 'Hvor fast blokeringen sidder',
      desc: 'Omfanget af fedt, sæberester, hårdt slam eller eventuel indtrængning af rødder.',
      icon: 'fa-solid fa-layer-group'
    },
    {
      title: 'Slamsugning eller TV-inspektion',
      desc: 'Behov for opsugning af bundfald i brønde eller kameraeftersyn ved mistanke om brud.',
      icon: 'fa-solid fa-camera-cctv'
    }
  ];

  const serviceTowns = [
    { name: 'Sommersted', isBase: true },
    { name: 'Jels', isBase: true },
    { name: 'Haderslev', isBase: false },
    { name: 'Vojens', isBase: false },
    { name: 'Vejen', isBase: false },
    { name: 'Kolding', isBase: false },
    { name: 'Gram', isBase: false }
  ];

  return (
    <div className="space-y-12">
      {/* Intro Banner */}
      <div className="rounded-xl border border-brand-orange-500/20 bg-gradient-to-br from-brand-navy-900 to-brand-navy-950 p-6 md:p-8 text-white shadow-lg relative overflow-hidden">
        <div className="absolute top-0 right-0 -mt-6 -mr-6 w-36 h-36 bg-brand-orange-500/10 rounded-full blur-2xl pointer-events-none" />
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6 relative z-10">
          <div className="space-y-3 max-w-2xl">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-brand-orange-500/20 border border-brand-orange-400/30 text-brand-orange-300 text-xs font-bold uppercase tracking-wider">
              <i className="fa-solid fa-award" /> Autoriseret Kloakmester
            </div>
            <h2 className="font-display text-xl sm:text-2xl md:text-3xl font-bold tracking-tight text-white leading-snug">
              Professionel kloakspuling der får vandet til at løbe frit
            </h2>
            <p className="text-brand-navy-200 text-base md:text-lg leading-relaxed">
              Er vandet begyndt at løbe langsomt, eller kommer der gurglende lyde og ubehagelig lugt fra afløbet? Entreprenør Jan Nørgaard udfører professionel kloakspuling for private og erhverv. Med mere end 20 års erfaring og en autoriseret kloakmester i virksomheden får du faglig hjælp til både problemet og årsagen bag.
            </p>
          </div>
          <div className="shrink-0 flex flex-col sm:flex-row md:flex-col gap-3 w-full md:w-auto">
            <a
              href="tel:+4520483818"
              className="inline-flex items-center justify-center gap-2 rounded bg-brand-orange-500 px-5 py-3 font-display text-sm font-bold uppercase tracking-wider text-white hover:bg-brand-orange-600 transition-colors shadow-md text-center"
            >
              <i className="fa-solid fa-phone" /> Ring 20 48 38 18
            </a>
            <Link
              to="/kontakt"
              className="inline-flex items-center justify-center gap-2 rounded border border-white/20 bg-white/10 px-5 py-3 font-display text-sm font-bold uppercase tracking-wider text-white hover:bg-white/20 transition-colors text-center"
            >
              <i className="fa-solid fa-calculator" /> Indhent tilbud
            </Link>
          </div>
        </div>
      </div>

      {/* Sektion 1: Hvornår kan spuling af kloak løse problemet? */}
      <section className="bg-white rounded-xl border border-brand-navy-100 p-6 md:p-8 shadow-xs">
        <div className="flex items-center gap-3 mb-4">
          <div className="h-10 w-10 rounded-lg bg-brand-orange-500/10 text-brand-orange-500 flex items-center justify-center text-xl shrink-0">
            <i className="fa-duotone fa-circle-question" />
          </div>
          <div>
            <span className="text-xs font-bold uppercase tracking-wider text-brand-orange-600">
              Diagnosticering & Årsager
            </span>
            <h3 className="font-display text-2xl font-bold text-brand-navy-900">
              Hvornår kan spuling af kloak løse problemet?
            </h3>
          </div>
        </div>

        <div className="text-brand-navy-700 leading-relaxed space-y-4 text-base md:text-lg">
          <p>
            En kloak bliver sjældent helt stoppet uden at vise tegn først. Vand, der står i vasken, tilbagevendende lugt eller lyde fra rørene kan skyldes fedt, sæberester, slam eller andet snavs. Hos Entreprenør Jan Nørgaard udfører vi professionel spuling af kloak, som genskaber gennemløbet og mindske risikoen for, at problemet hurtigt kommer igen.
          </p>
          <p>
            Ofte opdages problemer med kloakken i sammenhæng med ophobet overfladevand på grunden eller opstigende fugt, hvor en kombination med{' '}
            <Link
              to="/ydelser/draen-etablering-reparation"
              className="text-brand-orange-600 font-semibold underline decoration-brand-orange-500/50 underline-offset-2 hover:text-brand-orange-700 transition-colors"
            >
              etablering af dræn
            </Link>{' '}
            kan være nødvendig. Samtidig sikrer vi, at rør under indkørsler og flisearealer renses, så en eksisterende eller ny{' '}
            <Link
              to="/ydelser/belaegning"
              className="text-brand-orange-600 font-semibold underline decoration-brand-orange-500/50 underline-offset-2 hover:text-brand-orange-700 transition-colors"
            >
              belægning
            </Link>{' '}
            ikke tager skade af tilstoppet regnvandsafledning.
          </p>
        </div>

        {/* 4 Advarselstegn Kort */}
        <div className="mt-8 pt-6 border-t border-brand-navy-100">
          <h4 className="font-display text-base uppercase tracking-wider font-bold text-brand-navy-900 mb-4 flex items-center gap-2">
            <i className="fa-solid fa-triangle-exclamation text-brand-orange-500" />
            Tegn på at kloakken bør spules og renses:
          </h4>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {warningSigns.map((item, idx) => (
              <div
                key={idx}
                className="group relative rounded-lg border border-brand-navy-100 bg-brand-navy-50/50 p-4 transition-all hover:bg-white hover:shadow-md hover:border-brand-orange-500/30"
              >
                <div className="flex items-start gap-3.5">
                  <div className="h-10 w-10 shrink-0 rounded bg-brand-navy-900 text-brand-orange-400 flex items-center justify-center text-lg">
                    <i className={item.icon} />
                  </div>
                  <div>
                    <span className="text-[11px] font-bold uppercase tracking-wider text-brand-orange-600 bg-brand-orange-50 px-2 py-0.5 rounded">
                      {item.badge}
                    </span>
                    <h5 className="font-display text-base font-bold text-brand-navy-900 mt-1">
                      {item.title}
                    </h5>
                    <p className="mt-1 text-xs md:text-sm text-brand-navy-600 leading-relaxed">
                      {item.desc}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-6 rounded-lg bg-brand-navy-50 border border-brand-navy-200/80 p-5 flex items-start gap-3.5">
            <i className="fa-solid fa-circle-info text-brand-orange-500 text-lg mt-0.5 shrink-0" />
            <p className="text-sm md:text-base text-brand-navy-800 leading-relaxed font-medium">
              Vi tilpasser altid rensningen til rørenes tilstand og problemets omfang. En professionel rensning af kloak handler ikke kun om at skabe hul i en prop. Målet er at rense ledningen grundigt, så belægninger bliver fjernet, og spildevandet igen kan passere frit gennem systemet.
            </p>
          </div>
        </div>
      </section>

      {/* Sektion 2: Sådan foregår en grundig rensning af kloak (3-Trins Proces) */}
      <section className="bg-brand-navy-900 text-white rounded-xl p-6 md:p-8 shadow-md relative overflow-hidden">
        <div className="flex items-center gap-3 mb-4">
          <div className="h-10 w-10 rounded-lg bg-brand-orange-500 text-white flex items-center justify-center text-xl shrink-0">
            <i className="fa-duotone fa-pipe-valve" />
          </div>
          <div>
            <span className="text-xs font-bold uppercase tracking-wider text-brand-orange-400">
              Faglig Arbejdsgang
            </span>
            <h3 className="font-display text-2xl md:text-3xl font-bold tracking-tight text-white">
              Sådan foregår en grundig rensning af kloak
            </h3>
          </div>
        </div>

        <div className="text-brand-navy-200 text-base md:text-lg leading-relaxed space-y-4">
          <p>
            Kloakspuling udføres med vand under kontrolleret højt tryk. En specialdyse føres ind i ledningen, hvor vandet løsner aflejringer og fører materialet tilbage mod adgangsstedet. Metoden er effektiv, når arbejdet bliver tilpasset rørtype, dimension og kloakkens aktuelle tilstand.
          </p>
        </div>

        {/* 3-Trins Proces Visualisering */}
        <div className="mt-8 pt-6 border-t border-brand-navy-800">
          <h4 className="font-display text-sm uppercase tracking-wider font-bold text-brand-orange-400 mb-6 flex items-center gap-2">
            <i className="fa-solid fa-arrow-progress" /> Vores systematiske proces fra start til slut:
          </h4>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {processSteps.map((step, idx) => (
              <div
                key={idx}
                className="bg-brand-navy-800/80 rounded-lg p-5 border border-brand-navy-700/80 relative flex flex-col justify-between"
              >
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <span className="font-display text-3xl font-black text-brand-orange-500">
                      {step.step}
                    </span>
                    <span className="text-xs font-bold uppercase tracking-wider text-brand-orange-300 bg-brand-navy-900/80 px-2.5 py-1 rounded">
                      {step.phase}
                    </span>
                  </div>
                  <h5 className="font-display text-lg font-bold text-white mb-2.5 leading-snug">
                    {step.title}
                  </h5>
                  <p className="text-xs md:text-sm text-brand-navy-300 leading-relaxed">
                    {step.text}
                  </p>
                </div>
                <div className="mt-5 pt-3 border-t border-brand-navy-700 flex items-center gap-2 text-xs text-brand-orange-300 font-semibold">
                  <i className={`${step.icon} text-sm`} />
                  <span>Kvalitetssikret udførelse</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Sektion 3: Hvad kan du selv gøre, før kloakken skal renses? */}
      <section className="bg-white rounded-xl border border-brand-navy-100 p-6 md:p-8 shadow-xs">
        <div className="flex items-center gap-3 mb-4">
          <div className="h-10 w-10 rounded-lg bg-brand-orange-500/10 text-brand-orange-500 flex items-center justify-center text-xl shrink-0">
            <i className="fa-duotone fa-hand-holding-hand" />
          </div>
          <div>
            <span className="text-xs font-bold uppercase tracking-wider text-brand-orange-600">
              Gode Råd & Forebyggelse
            </span>
            <h3 className="font-display text-2xl font-bold text-brand-navy-900">
              Hvad kan du selv gøre, før kloakken skal renses?
            </h3>
          </div>
        </div>

        <div className="text-brand-navy-700 leading-relaxed space-y-4 text-base md:text-lg">
          <p>
            Et enkelt afløb kan nogle gange klares ved at rense rist og vandlås, men flere langsomme afløb på samme tid peger ofte på et problem længere ude i systemet. Undgå stærke kemikalier, og stop med at bruge installationerne, hvis vandet presses tilbage. Så begrænser du risikoen for følgeskader.
          </p>
        </div>

        {/* 4 Konkrete Råd */}
        <div className="mt-8 pt-6 border-t border-brand-navy-100">
          <h4 className="font-display text-sm uppercase tracking-wider font-bold text-brand-navy-900 mb-4 flex items-center gap-2">
            <i className="fa-solid fa-list-check text-brand-orange-500" />
            4 ting du selv kan gøre:
          </h4>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {preventionTips.map((tipItem, idx) => (
              <div
                key={idx}
                className="rounded-lg border border-brand-navy-100 bg-brand-navy-50/40 p-4 flex items-start gap-3.5"
              >
                <div className="h-9 w-9 shrink-0 rounded bg-white shadow-xs border border-brand-navy-200/60 text-brand-orange-500 flex items-center justify-center text-sm mt-0.5">
                  <i className={tipItem.icon} />
                </div>
                <div>
                  <h5 className="font-display text-sm md:text-base font-bold text-brand-navy-900 leading-snug">
                    {tipItem.tip}
                  </h5>
                  <p className="mt-1 text-xs md:text-sm text-brand-navy-600 leading-relaxed">
                    {tipItem.detail}
                  </p>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-6 bg-amber-50/80 border-l-4 border-amber-500 p-4 rounded-r-lg">
            <div className="flex items-start gap-3">
              <i className="fa-solid fa-shield-halved text-amber-600 mt-1 text-lg shrink-0" />
              <p className="text-sm md:text-base text-amber-900 leading-relaxed">
                Forebyggelse kan reducere mængden af belægninger, men det erstatter ikke faglig hjælp, når ledningen er blokeret. Har du brug for rens af kloak, vurderer vi, om kloakspuling er tilstrækkelig, eller om problemet bør undersøges nærmere med TV inspektion.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Sektion 4: Hvad afhænger prisen på kloakspuling af? */}
      <section className="bg-white rounded-xl border border-brand-navy-100 p-6 md:p-8 shadow-xs">
        <div className="max-w-3xl mb-6">
          <div className="flex items-center gap-2 mb-2">
            <div className="h-8 w-8 rounded-lg bg-brand-orange-500/10 text-brand-orange-600 flex items-center justify-center text-base">
              <i className="fa-duotone fa-calculator" />
            </div>
            <span className="text-xs font-bold uppercase tracking-wider text-brand-orange-600">
              Gennemskuelighed
            </span>
          </div>
          <h3 className="font-display text-2xl font-bold text-brand-navy-900">
            Hvad afhænger prisen på kloakspuling af?
          </h3>
          <p className="mt-2 text-brand-navy-700 text-base md:text-lg leading-relaxed">
            Prisen på kloakspuling afhænger blandt andet af ledningens længde, adgangsforholdene, hvor fast blokeringen sidder, og om der også er behov for slamsugning eller TV inspektion. Derfor tager vi udgangspunkt i din konkrete situation og forklarer, hvilken indsats der giver mening.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {priceFactors.map((factor, idx) => (
            <div
              key={idx}
              className="rounded-lg border border-brand-navy-100 bg-brand-navy-50/40 p-4 flex flex-col justify-between"
            >
              <div>
                <div className="h-9 w-9 rounded bg-brand-navy-900 text-brand-orange-400 flex items-center justify-center text-sm mb-3">
                  <i className={factor.icon} />
                </div>
                <h4 className="font-display text-base font-bold text-brand-navy-900 mb-1">
                  {factor.title}
                </h4>
                <p className="text-xs md:text-sm text-brand-navy-600 leading-relaxed">
                  {factor.desc}
                </p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Interne SEO links til beslægtede ydelser */}
      <section className="bg-white rounded-xl border border-brand-navy-200/90 p-6 md:p-8 shadow-xs">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-6 pb-4 border-b border-brand-navy-100">
          <div>
            <span className="text-xs font-bold uppercase tracking-wider text-brand-orange-600">
              Relaterede SEO ydelser
            </span>
            <h3 className="font-display text-xl md:text-2xl font-bold text-brand-navy-900 mt-0.5">
              Se også vores beslægtede fagområder
            </h3>
          </div>
          <p className="text-xs md:text-sm text-brand-navy-500">
            Samlet ekspertise inden for kloak, fugtsikring og anlægsarbejde
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Link
            to="/ydelser/draen-etablering-reparation"
            className="group p-5 rounded-xl border border-brand-navy-200/80 bg-brand-navy-50/40 hover:bg-white hover:border-brand-orange-400 hover:shadow-md transition-all flex flex-col justify-between"
          >
            <div>
              <div className="flex items-center justify-between mb-3">
                <div className="h-11 w-11 rounded-lg bg-brand-navy-900 text-brand-orange-400 group-hover:bg-brand-orange-500 group-hover:text-white transition-colors flex items-center justify-center text-xl shadow-xs">
                  <i className="fa-duotone fa-droplet-slash" />
                </div>
                <span className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-brand-orange-600 group-hover:translate-x-1 transition-transform">
                  Læs om dræn <i className="fa-solid fa-arrow-right text-[11px]" />
                </span>
              </div>
              <h4 className="font-display text-lg font-bold text-brand-navy-900 group-hover:text-brand-orange-600 transition-colors">
                Dræn – Etablering & Reparation
              </h4>
              <p className="mt-2 text-sm text-brand-navy-600 leading-relaxed">
                Beskyt sokkel, kælder og have mod opstigende grundvand og overfladevand med professionelt etablerede omfangsdræn og drænspuling.
              </p>
            </div>
          </Link>

          <Link
            to="/ydelser/belaegning"
            className="group p-5 rounded-xl border border-brand-navy-200/80 bg-brand-navy-50/40 hover:bg-white hover:border-brand-orange-400 hover:shadow-md transition-all flex flex-col justify-between"
          >
            <div>
              <div className="flex items-center justify-between mb-3">
                <div className="h-11 w-11 rounded-lg bg-brand-navy-900 text-brand-orange-400 group-hover:bg-brand-orange-500 group-hover:text-white transition-colors flex items-center justify-center text-xl shadow-xs">
                  <i className="fa-duotone fa-grid-horizontal" />
                </div>
                <span className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-brand-orange-600 group-hover:translate-x-1 transition-transform">
                  Læs om belægning <i className="fa-solid fa-arrow-right text-[11px]" />
                </span>
              </div>
              <h4 className="font-display text-lg font-bold text-brand-navy-900 group-hover:text-brand-orange-600 transition-colors">
                Belægning & Flisearbejde
              </h4>
              <p className="mt-2 text-sm text-brand-navy-600 leading-relaxed">
                Kvalitetsbelægninger til terrasser, indkørsler og gangstier. Vi sikrer korrekt bærelag, fald og retablering efter gravearbejde.
              </p>
            </div>
          </Link>
        </div>
      </section>

      {/* Sektion 5: Lokal kloakspuling i Sommersted og omegn */}
      <section className="rounded-xl border border-brand-navy-200 bg-gradient-to-br from-white to-brand-navy-50/50 p-6 md:p-8 shadow-sm">
        <div className="flex flex-col lg:flex-row gap-8 items-start lg:items-center justify-between">
          <div className="space-y-4 max-w-2xl">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-brand-navy-900 text-white text-xs font-bold uppercase tracking-wider">
              <i className="fa-solid fa-location-dot text-brand-orange-400" />
              Lokal dækning i Sønderjylland
            </div>
            <h3 className="font-display text-2xl md:text-3xl font-bold text-brand-navy-900">
              Lokal kloakspuling i Sommersted og omegn
            </h3>
            <p className="text-brand-navy-700 text-base md:text-lg leading-relaxed">
              Vi har base i Sommersted og hjælper kunder i området omkring Jels samt i Haderslev, Vojens, Vejen, Kolding og Gram. Uanset om du har brug for kloakspuling i et privat hjem, en udlejningsejendom eller en virksomhed, møder vi opgaven med mere end 20 års praktisk erfaring.
            </p>
            <p className="text-brand-navy-700 text-base md:text-lg leading-relaxed">
              Som lokal entreprenør kan vi se kloakopgaven i sammenhæng med jordarbejde,{' '}
              <Link
                to="/ydelser/draen-etablering-reparation"
                className="text-brand-orange-600 font-semibold underline decoration-brand-orange-500/50 underline-offset-2 hover:text-brand-orange-700 transition-colors"
              >
                etablering af dræn
              </Link>{' '}
              og retablering af fliser og{' '}
              <Link
                to="/ydelser/belaegning"
                className="text-brand-orange-600 font-semibold underline decoration-brand-orange-500/50 underline-offset-2 hover:text-brand-orange-700 transition-colors"
              >
                belægning
              </Link>{' '}
              omkring ejendommen. Det er en fordel, når rensningen ikke står alene, eller når adgang og terræn kræver en praktisk løsning, der tager hensyn til resten af grunden.
            </p>
          </div>

          <div className="w-full lg:w-72 shrink-0 bg-white p-5 rounded-lg border border-brand-navy-200 shadow-xs">
            <h4 className="font-display text-xs uppercase tracking-wider font-bold text-brand-navy-500 mb-3">
              Vi dækker følgende byer:
            </h4>
            <div className="flex flex-wrap gap-2">
              {serviceTowns.map((town, idx) => (
                <span
                  key={idx}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded text-xs font-bold uppercase tracking-wider ${
                    town.isBase
                      ? 'bg-brand-orange-500 text-white shadow-xs'
                      : 'bg-brand-navy-100 text-brand-navy-800'
                  }`}
                >
                  <i className={`fa-solid ${town.isBase ? 'fa-house-flag' : 'fa-location-dot text-brand-orange-600'}`} />
                  {town.name}
                  {town.isBase && <span className="text-[10px] opacity-80">(Base)</span>}
                </span>
              ))}
            </div>

            <div className="mt-5 pt-4 border-t border-brand-navy-100">
              <Link
                to="/kontakt"
                className="w-full inline-flex items-center justify-center gap-2 rounded bg-brand-navy-900 py-2.5 font-display text-xs font-bold uppercase tracking-wider text-white hover:bg-brand-navy-800 transition-colors text-center"
              >
                Kontakt Jan Nørgaard
                <i className="fa-solid fa-arrow-right text-brand-orange-400" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Sektion 6: Få den rette hjælp, før problemet vokser */}
      <section className="bg-brand-orange-500 rounded-xl p-6 md:p-8 text-white shadow-md relative overflow-hidden">
        <div className="absolute top-0 right-0 -mt-8 -mr-8 w-40 h-40 bg-white/10 rounded-full blur-2xl pointer-events-none" />
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6 relative z-10">
          <div className="space-y-3 max-w-2xl">
            <span className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider bg-white/20 text-white px-2.5 py-1 rounded">
              <i className="fa-solid fa-phone-volume" /> Hurtig Udrykning & Rådgivning
            </span>
            <h3 className="font-display text-2xl md:text-3xl font-bold tracking-tight text-white">
              Få den rette hjælp, før problemet vokser
            </h3>
            <p className="text-white/90 text-base md:text-lg leading-relaxed">
              Har du langsomme afløb, tilbagevendende lugt eller mistanke om en blokering, er det en god ide at reagere, før kloakken stopper helt. Kontakt Entreprenør Jan Nørgaard og fortæl, hvad du oplever. Så hjælper vi med at vurdere behovet for kloakspuling og den rigtige vej videre.
            </p>
          </div>
          <div className="shrink-0 flex flex-col sm:flex-row md:flex-col gap-3 w-full md:w-auto">
            <a
              href="tel:+4520483818"
              className="inline-flex items-center justify-center gap-2 rounded bg-brand-navy-900 px-6 py-3.5 font-display text-sm font-bold uppercase tracking-wider text-white hover:bg-brand-navy-800 transition-colors shadow-lg text-center"
            >
              <i className="fa-solid fa-phone text-brand-orange-400" /> Ring 20 48 38 18
            </a>
            <Link
              to="/kontakt"
              className="inline-flex items-center justify-center gap-2 rounded bg-white text-brand-navy-950 px-6 py-3.5 font-display text-sm font-bold uppercase tracking-wider hover:bg-brand-navy-50 transition-colors text-center shadow-md"
            >
              <i className="fa-solid fa-envelope text-brand-orange-600" /> Kontakt os i dag
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
